import React, { useState } from 'react';
import useSWR from 'swr';
import { adminListReviews, adminCreateReview, adminDeleteReview, uploadFileToStorage } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { Plus, Trash2, Loader2, Upload } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';

export default function AdminReviews() {
  const { data: reviews, mutate, isLoading } = useSWR('adminReviews', adminListReviews);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  const [image, setImage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!image) return;
    setLoading(true);
    try {
      await adminCreateReview({ image });
      toast({ title: 'Review added successfully' });
      setOpen(false);
      setImage('');
      mutate();
    } catch (err) {
      toast({ title: 'Error adding review', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    try {
      await adminDeleteReview(id);
      toast({ title: 'Review deleted' });
      mutate();
    } catch (err) {
      toast({ title: 'Error deleting review', variant: 'destructive' });
    }
  };

  if (isLoading) {
    return <div className="p-6 space-y-4"><Skeleton className="h-10 w-48" /><Skeleton className="h-[400px] w-full" /></div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-serif text-neutral-900">Reviews</h1>
        
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#b8892d] hover:bg-[#a67b28] text-white">
              <Plus className="mr-2 h-4 w-4" /> Add Review
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add New Review</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Image URL or Upload</Label>
                <div className="flex items-center gap-2">
                  <Input value={image} onChange={(e) => setImage(e.target.value)} placeholder="Image URL" required={!image} disabled={uploading} />
                  <span className="text-neutral-500 text-sm">or</span>
                  <div className="relative">
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          if (file.size > 5 * 1024 * 1024) {
                            toast({ 
                              title: 'File is too large', 
                              description: 'Please upload an image under 5MB.', 
                              variant: 'destructive' 
                            });
                            return;
                          }
                          setUploading(true);
                          try {
                            const url = await uploadFileToStorage(file, 'reviews');
                            setImage(url);
                            toast({ title: 'Image uploaded successfully' });
                          } catch (err) {
                            console.error('Upload error:', err);
                            toast({ title: 'Error uploading image', variant: 'destructive' });
                          } finally {
                            setUploading(false);
                          }
                        }
                      }} 
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      disabled={uploading}
                    />
                    <Button type="button" variant="outline" className="gap-2" disabled={uploading}>
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                      Upload
                    </Button>
                  </div>
                </div>
                {image && image.startsWith('data:image') && (
                  <div className="mt-2 text-xs text-amber-600">Base64 Image URL</div>
                )}
                {image && image.startsWith('http') && (
                  <div className="mt-2 text-xs text-green-600 font-medium">Stored on Firebase Storage</div>
                )}
                {image && (
                  <div className="mt-4">
                    <img src={image} alt="Preview" className="w-full h-auto max-h-64 object-contain border border-neutral-200 rounded" />
                  </div>
                )}
              </div>
              <Button type="submit" className="w-full bg-[#b8892d] hover:bg-[#a67b28] text-white" disabled={loading || uploading || !image}>
                {loading ? 'Adding...' : 'Add Review'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {reviews?.map((review) => (
          <div key={review.id} className="relative group rounded border border-neutral-200 overflow-hidden bg-white">
            <img src={review.image} alt="Review" className="w-full h-auto object-cover aspect-[9/16]" />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <Button variant="destructive" size="icon" onClick={() => handleDelete(review.id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
        {reviews?.length === 0 && (
          <div className="col-span-full py-12 text-center text-neutral-500 bg-white border border-neutral-200 rounded">
            No reviews found. Add one!
          </div>
        )}
      </div>
    </div>
  );
}
